// Hook de métricas do sistema — encapsula o polling de GET /api/system/metrics
// (host/GPU/docker/serviços), histórico de sparklines e o estado de pausa/forbidden.
// Extraído do antigo TecnologiaPanel para ser compartilhado entre SystemMonitor
// (aba Monitor) e ArchitectureDiagram (aba Arquitetura, que lê o status vivo).
// A flag `enabled` evita polling quando nenhuma das duas abas está ativa.
import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'

// ── Tipos (espelham a resposta de /api/system/metrics) ──
export interface SvcHealth { status: string; [k: string]: unknown }
export interface HostMetrics {
  hostname: string; uptime_s: number
  cpu: { cores: number; usage_pct: number | null; load: number[] }
  mem: { total_b: number; used_b: number; available_b: number; usage_pct: number }
  swap: { total_b: number; used_b: number }
  disk: { mount: string; total_b: number; used_b: number; usage_pct: number }[]
}
export interface GpuMetrics { card: string; label: string; busy_pct: number; vram_used_b: number; vram_total_b: number; temp_c: number | null }
export interface DockerMetrics { name: string; state: string; status: string; health: string | null; cpu_pct: number | null; mem_used_b: number | null; mem_limit_b: number | null; restarts: number | null; uptime_s: number | null }
export interface Metrics {
  ok: boolean; ts: string
  host: HostMetrics | null
  gpu: GpuMetrics[]
  docker: DockerMetrics[]
  services: { exporter?: SvcHealth; mysql?: SvcHealth; oracle?: SvcHealth; sidecar?: SvcHealth }
}

const POLL_MS = 5000
const HIST_LEN = 40

// ── Formatadores compartilhados ──
export function fmtBytes(n: number | null | undefined): string {
  if (n == null) return '—'
  if (n >= 1e9) return (n / 1024 ** 3).toFixed(1) + 'G'
  if (n >= 1e6) return (n / 1024 ** 2).toFixed(0) + 'M'
  if (n >= 1e3) return (n / 1024).toFixed(0) + 'K'
  return n + 'B'
}
export function fmtUptime(s: number | null | undefined): string {
  if (s == null) return '—'
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60)
  if (d > 0) return `${d}d${h}h`
  if (h > 0) return `${h}h${m}m`
  return `${m}m`
}
export function dotColor(status: string | null | undefined): string {
  if (status === 'healthy') return 'bg-emerald-500'
  if (status === 'starting') return 'bg-amber-500'
  if (status === 'disabled') return 'bg-slate-400'
  return 'bg-red-500'
}

export interface UseSystemMetrics {
  metrics: Metrics | null
  error: string | null
  forbidden: boolean
  paused: boolean
  setPaused: React.Dispatch<React.SetStateAction<boolean>>
  lastUpdate: Date | null
  cpuHist: number[]; memHist: number[]; gpuHist: number[]
  fetchMetrics: () => Promise<void>
}

export function useSystemMetrics({ enabled }: { enabled: boolean }): UseSystemMetrics {
  const { authenticatedFetch } = useAuth()
  const [metrics, setMetrics] = useState<Metrics | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)
  const [paused, setPaused] = useState(false)
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)
  const [cpuHist, setCpuHist] = useState<number[]>([])
  const [memHist, setMemHist] = useState<number[]>([])
  const [gpuHist, setGpuHist] = useState<number[]>([])
  const pausedRef = useRef(paused)
  pausedRef.current = paused

  const fetchMetrics = useCallback(async () => {
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.SYSTEM_METRICS)
      if (r.status === 403) { setForbidden(true); return }
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const m: Metrics = await r.json()
      setMetrics(m)
      setError(null)
      setLastUpdate(new Date())
      const push = (arr: number[], v: number) => [...arr, v].slice(-HIST_LEN)
      if (m.host?.cpu.usage_pct != null) setCpuHist(a => push(a, m.host!.cpu.usage_pct!))
      if (m.host?.mem.usage_pct != null) setMemHist(a => push(a, m.host!.mem.usage_pct))
      const dgpu = (m.gpu || []).slice().sort((a, b) => b.vram_total_b - a.vram_total_b)[0]
      if (dgpu) setGpuHist(a => push(a, dgpu.busy_pct))
    } catch (e) {
      setError((e as Error).message)
    }
  }, [authenticatedFetch])

  useEffect(() => {
    if (!enabled || paused) return
    fetchMetrics()
    const id = setInterval(() => { if (!pausedRef.current) fetchMetrics() }, POLL_MS)
    return () => clearInterval(id)
  }, [enabled, paused, fetchMetrics])

  return { metrics, error, forbidden, paused, setPaused, lastUpdate, cpuHist, memHist, gpuHist, fetchMetrics }
}
